/**
 * Session Management Hook
 *
 * Provides access to the current user session state and authentication context.
 * This hook must be used within a SessionProvider component and offers a
 * type-safe way to access user authentication data throughout the application.
 *
 * @returns {SessionContextType} Session context containing user data and auth methods
 *
 * @throws {Error} When used outside of SessionProvider context
 *
 * @example
 * ```tsx
 * import { useSession } from '@/hooks/useSession';
 *
 * function MyComponent() {
 *   const { user, session, signOut } = useSession();
 *
 *   if (!user) {
 *     return <div>Please log in</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user.email}!</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @see SessionProvider
 * @see SessionContext
 *
 * @since 1.0.0
 * @author Aegis Development Team
 */

import { useContext } from "react";
import { SessionContext } from "@/contexts/SessionContext";

/**
 * Hook to access session context
 * Must be used within SessionProvider
 */
export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return ctx;
}
